import React, { useState } from 'react';
import { useDispatch } from 'react-redux';
import { IconButton, Menu, MenuItem, ListItemIcon, ListItemText, Tooltip, Box } from '@mui/material';
import LanguageIcon from '@mui/icons-material/Language';
import { useSnackbar } from 'notistack';
import api from '../api';
import { updateUserProfile } from '../store/auth/userSlice';
import { useI18n } from '../hooks/useI18n';

const LANGUAGES = [
    { code: 'it', label: 'Italiano', flag: '/it.svg' },
    { code: 'en', label: 'English', flag: '/en.svg' }
];

export default function LanguageSelector() {
    const dispatch = useDispatch();
    const { enqueueSnackbar } = useSnackbar();
    const { t, i18n, isEnabled } = useI18n('common');

    const [anchorEl, setAnchorEl] = useState(null);
    const [loading, setLoading] = useState(false);
    const open = Boolean(anchorEl);


    if (!isEnabled) {
        return null;
    }
    
    const handleOpen = (event) => {
        setAnchorEl(event.currentTarget);
    };

    const handleClose = () => {
        setAnchorEl(null);
    };

    const handleLanguageChange = async (languageCode) => {
        if (languageCode === i18n.language) {
            handleClose();
            return;
        }

        setLoading(true);
        try {
            await i18n.changeLanguage(languageCode);

            await api.patch('/users/language/', {
                preferred_language: languageCode
            });

            dispatch(updateUserProfile({
                profile: {
                    preferred_language: languageCode
                }
            }));

            enqueueSnackbar(t('messages.languageUpdated'), { variant: 'success' });

        } catch (error) {
            console.error('Errore durante il cambio lingua:', error);
            enqueueSnackbar(t('messages.languageError'), { variant: 'error' });
        } finally {
            setLoading(false);
            handleClose();
        }
    };

    const currentLang = LANGUAGES.find(lang => lang.code === i18n.language) || LANGUAGES[0];

    return (
        <>
            <Tooltip title={t(`language.${currentLang.code}`, { defaultValue: currentLang.label })}>
                <IconButton
                    onClick={handleOpen}
                    size="small"
                    disabled={loading}
                    sx={{
                        color: 'text.primary',
                        '&:hover': {
                            bgcolor: 'action.hover'
                        }
                    }}
                >
                    <LanguageIcon fontSize="small" />
                </IconButton>
            </Tooltip>

            <Menu
                anchorEl={anchorEl}
                open={open}
                onClose={handleClose}
                anchorOrigin={{ vertical: 'bottom', horizontal: 'right' }}
                transformOrigin={{ vertical: 'top', horizontal: 'right' }}
                PaperProps={{
                    sx: {
                        mt: 1.5,
                        minWidth: 180,
                        borderRadius: 2,
                        boxShadow: 3
                    }
                }}
            >
                {LANGUAGES.map((lang) => (
                    <MenuItem
                        key={lang.code}
                        onClick={() => handleLanguageChange(lang.code)}
                        selected={lang.code === i18n.language}
                        disabled={loading}
                    >
                        <ListItemIcon>
                            <Box
                                component="img"
                                src={lang.flag}
                                alt={lang.label}
                                sx={{
                                    width: 24,
                                    height: 24,
                                    objectFit: 'contain',
                                    borderRadius: '4px'
                                }}
                            />
                        </ListItemIcon>
                        <ListItemText
                            primary={t(`language.${lang.code}`, { defaultValue: lang.label })}
                            primaryTypographyProps={{
                                fontWeight: lang.code === i18n.language ? 600 : 400
                            }}
                        />
                    </MenuItem>
                ))}
            </Menu>
        </>
    );
}